/**
 * Dot-matrix field for the login hero panels: the continental US in Albers,
 * zoomed and cropped so the lattice bleeds off every edge of the panel.
 *
 * The outline and projection are the same ones behind the Convergence hero
 * (`events/convergencerl/_lib/us-map.ts`). They are restated here rather than
 * shared because that folder is route-private and this is a dev-only preview.
 */

export interface Point {
  x: number;
  y: number;
}

/** Continental US outline as flattened lon/lat pairs, clockwise from Cape Flattery. */
const OUTLINE_FLAT = [
  -124.7, 48.4, -123.0, 49.0, -117.0, 49.0, -110.0, 49.0, -104.0, 49.0, -97.2,
  49.0, -95.15, 49.4, -95.15, 48.9, -93.5, 48.6, -91.5, 48.1, -89.5, 48.0,
  -90.9, 46.6, -88.0, 46.5, -85.0, 46.75, -84.6, 46.0, -83.0, 46.0, -82.4, 45.0,
  -82.5, 43.0, -83.1, 42.0, -82.7, 41.7, -80.5, 42.3, -79.0, 42.8, -77.0, 43.3,
  -76.5, 44.1, -74.7, 45.0, -71.5, 45.0, -69.2, 47.45, -67.8, 47.1, -67.0, 45.7,
  -67.1, 44.8, -69.0, 44.0, -70.7, 43.1, -70.8, 42.3, -70.0, 41.8, -71.4, 41.5,
  -72.9, 41.2, -74.0, 40.7, -74.4, 39.4, -75.0, 38.8, -75.1, 38.0, -76.0, 37.2,
  -76.0, 36.9, -75.5, 35.6, -76.5, 34.7, -78.0, 33.9, -79.2, 33.1, -80.8, 32.1,
  -81.5, 31.1, -81.4, 30.3, -81.3, 29.3, -80.6, 28.3, -80.1, 26.9, -80.1, 25.8,
  -81.1, 25.2, -81.8, 26.0, -82.7, 27.5, -82.7, 28.9, -83.6, 29.9, -84.3, 30.1,
  -85.4, 29.7, -86.5, 30.4, -88.0, 30.3, -89.4, 29.2, -90.2, 29.1, -91.5, 29.5,
  -92.5, 29.6, -93.8, 29.7, -94.8, 29.3, -95.3, 28.9, -96.5, 28.4, -97.4, 27.3,
  -97.2, 26.1, -97.8, 26.0, -99.1, 26.4, -100.0, 28.0, -101.4, 29.8, -102.4,
  29.8, -103.3, 29.0, -104.5, 29.7, -106.5, 31.8, -108.2, 31.3, -111.0, 31.3,
  -114.8, 32.5, -117.1, 32.5, -118.4, 33.7, -120.5, 34.5, -121.9, 36.6, -122.4,
  37.8, -124.0, 40.4, -124.2, 42.0, -124.0, 44.6, -124.1, 46.3,
];

const RAD = Math.PI / 180;
const PHI_1 = 29.5 * RAD;
const PHI_2 = 45.5 * RAD;
const LAT_0 = 37.5 * RAD;
const LON_0 = -96 * RAD;
const CONE = (Math.sin(PHI_1) + Math.sin(PHI_2)) / 2;
const CONST_C = Math.cos(PHI_1) ** 2 + 2 * CONE * Math.sin(PHI_1);
const RHO_0 = Math.sqrt(CONST_C - 2 * CONE * Math.sin(LAT_0)) / CONE;

/** Albers equal-area conic. Its curved northern border is what makes the field read as the US. */
function albers(lon: number, lat: number): Point {
  const rho = Math.sqrt(CONST_C - 2 * CONE * Math.sin(lat * RAD)) / CONE;
  const theta = CONE * (lon * RAD - LON_0);
  return { x: rho * Math.sin(theta), y: RHO_0 - rho * Math.cos(theta) };
}

function pointInPolygon(px: number, py: number, poly: Point[]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const a = poly[i] as Point;
    const b = poly[j] as Point;
    if (
      a.y > py !== b.y > py &&
      px < ((b.x - a.x) * (py - a.y)) / (b.y - a.y) + a.x
    ) {
      inside = !inside;
    }
  }
  return inside;
}

/** Cities the panels pin marks to. Snapped onto the lattice before use. */
export const CITIES = {
  Austin: [-97.74, 30.27],
  Houston: [-95.37, 29.76],
  StLouis: [-90.2, 38.63],
  Nashville: [-86.78, 36.16],
  Birmingham: [-86.8, 33.52],
  Atlanta: [-84.39, 33.75],
  Savannah: [-81.09, 32.08],
  Jacksonville: [-81.66, 30.33],
  Raleigh: [-78.64, 35.78],
  Richmond: [-77.44, 37.54],
  Philadelphia: [-75.17, 39.95],
} satisfies Record<string, readonly [number, number]>;

export type CityName = keyof typeof CITIES;

/**
 * The design's crop, in the units of the Figma comp: a 700x884 window taken out
 * of a country fitted to 1538x960, with its top-left corner at (700, 40). That
 * window is the eastern US, the only crop at this zoom where the Great Lakes,
 * New England and Florida all survive.
 */
const DESIGN = {
  panelW: 700,
  panelH: 884,
  fullW: 1538,
  fullH: 960,
  originX: 700,
  originY: 40,
  columns: 146,
};

export interface DotField {
  /** Flat x,y pairs in panel pixels. Flat because this is the hot path in the draw loop. */
  dots: Float32Array;
  radius: number;
  pitch: number;
  /** Lattice-snapped panel position for every city, keyed by name. */
  cities: Record<CityName, Point>;
}

/**
 * Build the field for a panel of the given size. `scale` drives off panel
 * height so the comp is pixel-identical at the 1440x900 design viewport and
 * stays proportional elsewhere; the crop is re-centred so a wider or narrower
 * panel reveals or hides map evenly on both sides rather than drifting east.
 */
export function buildDotField(
  panelW: number,
  panelH: number,
  scale: number,
): DotField {
  const fullW = DESIGN.fullW * scale;
  const fullH = DESIGN.fullH * scale;
  const pitch = fullW / DESIGN.columns;
  const rowStep = pitch * 0.866;
  const originX = DESIGN.originX * scale + (DESIGN.panelW * scale - panelW) / 2;
  const originY = DESIGN.originY * scale + (DESIGN.panelH * scale - panelH) / 2;

  const projected = OUTLINE_FLAT.reduce<Point[]>((acc, value, index) => {
    if (index % 2 === 0) {
      acc.push(albers(value, OUTLINE_FLAT[index + 1] as number));
    }
    return acc;
  }, []);
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  for (const p of projected) {
    minX = Math.min(minX, p.x);
    maxX = Math.max(maxX, p.x);
    minY = Math.min(minY, p.y);
    maxY = Math.max(maxY, p.y);
  }
  const s = fullW / (maxX - minX);
  // Albers y grows northward and screen y grows downward, so flip it here.
  const toFull = (p: Point): Point => ({
    x: (p.x - minX) * s,
    y: (maxY - p.y) * s,
  });
  const poly = projected.map(toFull);

  const dots: number[] = [];
  const rows = Math.ceil(fullH / rowStep) + 1;
  for (let row = 0; row < rows; row++) {
    const y = pitch / 2 + row * rowStep;
    const offset = row % 2 ? pitch / 2 : 0;
    for (let x = pitch / 2 + offset; x < fullW; x += pitch) {
      const px = x - originX;
      const py = y - originY;
      if (
        px < -pitch ||
        px > panelW + pitch ||
        py < -pitch ||
        py > panelH + pitch
      ) {
        continue;
      }
      if (pointInPolygon(x, y, poly)) {
        dots.push(px, py);
      }
    }
  }

  // Snap each city onto its nearest lattice cell: a mark sitting between cells
  // reads as a bug, snapped it reads as one cell of the field lighting up.
  const cities = {} as Record<CityName, Point>;
  for (const name of Object.keys(CITIES) as CityName[]) {
    const [lon, lat] = CITIES[name];
    const full = toFull(albers(lon, lat));
    const row = Math.round((full.y - pitch / 2) / rowStep);
    const offset = row % 2 ? pitch / 2 : 0;
    const col = Math.round((full.x - pitch / 2 - offset) / pitch);
    cities[name] = {
      x: pitch / 2 + offset + col * pitch - originX,
      y: pitch / 2 + row * rowStep - originY,
    };
  }

  return {
    dots: Float32Array.from(dots),
    radius: pitch * 0.19,
    pitch,
    cities,
  };
}
